import {
  Component,
  DoCheck,
  EventEmitter,
  HostBinding,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnInit,
  Output,
} from '@angular/core';
import {
  StorageSelectorFilter,
  StorageSelectorType,
  StorageUtilsService,
} from '../../services/storage-utils.service';
import { StorageService } from '../../services/storage.service';
import { StorageFile } from '../../services/storage-utils.service';
import { ListItem } from 'src/app/components/scrolling-list';

@Component({
  selector: 'thumbnail-image',
  templateUrl: './thumbnail-image.component.html',
  styleUrls: ['./thumbnail-image.component.scss'],
  standalone: false,
})
export class ThumbnailImageComponent implements OnInit, DoCheck {
  @Input() folderPath: string = '';
  @Input() file: ListItem<StorageFile>;
  @Input() index: number;
  @Input({ required: true }) selectorType: StorageSelectorType;
  @Input({ required: true }) selectorFilter: StorageSelectorFilter;
  @Input({ required: true }) storageFull: boolean;

  @HostBinding('class.small')
  @Input()
  small = false;

  @Output() fileSelect: EventEmitter<any> = new EventEmitter<any>();

  fileDiffer: KeyValueDiffer<string, any>;
  classes = [];
  imgClasses = '';
  isDisabled = false;
  isPlaceholder = true;
  isVideo = false;
  isImage = false;
  isFolder = false;
  imgSrc =
    'https://widgets.risevision.com/apps/logos/storage-file-icon%402x.png';

  constructor(
    private storageService: StorageService,
    public storageUtilsService: StorageUtilsService,
    private keyValueDiffers: KeyValueDiffers,
  ) {}

  ngOnInit(): void {
    this.fileDiffer = this.keyValueDiffers.find(this.file).create();
  }

  ngDoCheck(): void {
    const fileChanges = this.fileDiffer.diff(this.file);
    if (fileChanges) {
      this.fileChanged();
    }
  }

  fileChanged() {
    this.isVideo = false;
    this.isImage = false;
    this.isFolder = false;
    this.classes = ['list-item'];
    this.isPlaceholder = false;
    if (this.file.selected) {
      this.classes.push('list-item--selected');
    }

    if (this.storageUtilsService.fileIsFolder(this.file.item)) {
      this.isFolder = true;
      this.classes.push('list-item_folder');
      if (this.storageUtilsService.fileIsTrash(this.file.item)) {
        this.classes.push('list-item_trash');
      }
    } else {
      this.classes.push('single-item');
      this.classes.push('list-type_image');
      if ((this.file.item as StorageFile).isThrottled) {
        this.classes.push('list-item--throttled');
      } else {
        if (
          !this.storageService.canSelect(
            this.file,
            this.storageFull,
            this.selectorType,
            this.selectorFilter,
          ) ||
          this.storageService.isDisabled(
            this.file,
            this.storageFull,
            this.selectorType,
            this.selectorFilter,
          )
        ) {
          this.classes.push('list-item--disabled');
          this.isDisabled = true;
        } else {
          this.isDisabled = false;
        }

        if (
          (this.file.item as StorageFile).metadata &&
          (this.file.item as StorageFile).metadata.thumbnail
        ) {
          this.isPlaceholder = false;
          this.isImage = true;
          this.imgSrc =
            (this.file.item as StorageFile).metadata.thumbnail +
            '?_=' +
            ((this.file.item as StorageFile).timeCreated &&
              (this.file.item as StorageFile).timeCreated.value);
          if (this.imgSrc.indexOf('http://') === 0) {
            this.imgSrc = this.imgSrc.replace('http://', 'https://');
          }
        } else if (this.storageUtilsService.fileIsImage(this.file.item)) {
          this.isPlaceholder = true;
          this.imgSrc = this.getExtensionImage('image');
        } else if (this.storageUtilsService.fileIsVideo(this.file.item)) {
          this.isPlaceholder = false;
          this.isVideo = true;
          this.imgSrc =
            (this.file.item as StorageFile).mediaLink +
            ((this.file.item as StorageFile).mediaLink.indexOf('?') > -1
              ? '&t=3'
              : '?t=3');
        } else if (this.storageUtilsService.fileIsDocument(this.file.item)) {
          this.isPlaceholder = true;
          const extension = this.storageUtilsService.getFileType(
            this.file.item.name,
          );
          this.imgSrc = this.getExtensionsDocument(extension);
        } else if (this.storageUtilsService.fileIsScript(this.file.item)) {
          this.isPlaceholder = true;
          this.imgSrc = this.getExtensionImage('script');
        } else if (this.storageUtilsService.fileIsFont(this.file.item)) {
          this.isPlaceholder = true;
          this.imgSrc = this.getExtensionImage('font');
        } else {
          this.isPlaceholder = true;
          this.imgSrc = this.getExtensionImage('other');
        }
      }
    }

    this.imgClasses =
      this.isDisabled && !(this.file.item as StorageFile).isThrottled
        ? 'disabled'
        : '';
  }

  private getExtensionsDocument(extension: string) {
    const ext = extension.toLowerCase();
    if (['pptx', 'ppt'].includes(ext)) return this.getExtensionImage('ppt');
    if (['docx', 'doc'].includes(ext)) return this.getExtensionImage('doc');
    if (['xlsx', 'xls'].includes(ext)) return this.getExtensionImage('xls');
    return this.getExtensionImage(ext);
  }

  private getExtensionImage(extension: string) {
    const ext = extension.toLowerCase();
    return `../images/thumbnails/${ext}.svg`;
  }

  getFileTimeCreated() {
    return (this.file.item as StorageFile).timeCreated?.value;
  }

  getFileSize() {
    return (this.file.item as StorageFile).size;
  }
}
